"use client";

import { useEffect, useState } from "react";
import { SCOPED_PANEL_TRANSITION_DURATION_MS } from "./constants";

interface UseScopedPanelOverlayVisibilityOptions {
	durationMs?: number;
	/** When true, mount/unmount instantly (e.g. reduced motion). */
	disabled?: boolean;
}

interface ScopedPanelOverlayVisibility {
	mounted: boolean;
	visible: boolean;
}

export function useScopedPanelOverlayVisibility(
	open: boolean,
	options: UseScopedPanelOverlayVisibilityOptions = {},
): ScopedPanelOverlayVisibility {
	const durationMs =
		options.durationMs ?? SCOPED_PANEL_TRANSITION_DURATION_MS;
	const disabled = options.disabled ?? false;
	const [mounted, setMounted] = useState(open);
	const [visible, setVisible] = useState(open);

	useEffect(() => {
		if (disabled) {
			setMounted(open);
			setVisible(open);
			return;
		}

		if (open) {
			setMounted(true);
			const enterFrame = requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					setVisible(true);
				});
			});
			return () => {
				cancelAnimationFrame(enterFrame);
			};
		}

		setVisible(false);
		const exitTimer = window.setTimeout(() => {
			setMounted(false);
		}, durationMs);
		return () => {
			window.clearTimeout(exitTimer);
		};
	}, [disabled, durationMs, open]);

	return { mounted, visible };
}
